import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import type { ActionPlan } from '../../../api/workEnvironmentTypes';
import { useCommentOnPlanMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { TextField } from '../components/FormParts';
import { reviewStatusText, ReviewMissing } from '../planView';
import { dayAndTime } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; plan: ActionPlan | null; reviewMissing: ReviewMissing };

/** The safety representative approves the plan, and can comment on what should change */
export const PlanReviewCard = ({ surveyId, plan, reviewMissing }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const [text, setText] = useState('');
  const comment = useCommentOnPlanMutation(surveyId);
  const status = reviewStatusText(plan, reviewMissing, t, i18n.language);

  return (
    <section aria-labelledby="plan-review-heading" className={`${process.card} ${process.sideCard}`}>
      <Heading level={2} data-size="xs" id="plan-review-heading">
        {t('workEnvironmentPlan.review.title')}
      </Heading>
      {status && (
        <Paragraph data-size="sm" className={process.subtle}>
          {status}
        </Paragraph>
      )}
      {plan && plan.comments.length > 0 && (
        <ol className={classes.log}>
          {plan.comments.map((entry) => (
            <li key={entry.id} className={classes.logEntry} data-type="event">
              <Paragraph data-size="sm">{entry.text}</Paragraph>
              <Paragraph data-size="xs" className={process.subtle}>
                {t('workEnvironmentPlan.review.by', {
                  email: entry.author ?? '',
                  role: t(`workEnvironmentRisk.activity.roles.${entry.role}`),
                  when: dayAndTime(entry.at, t, i18n.language),
                })}
              </Paragraph>
            </li>
          ))}
        </ol>
      )}
      {plan && !plan.approved && may('reviewPlan') && (
        <>
          <TextField
            id="plan-comment"
            label={t('workEnvironmentPlan.review.comment')}
            value={text}
            onChange={setText}
          />
          {comment.isError && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentPlan.review.failed')}</Paragraph>
            </Alert>
          )}
          <div>
            <Button
              variant="secondary"
              data-size="sm"
              loading={comment.isPending}
              onClick={() => comment.mutate({ planId: plan.id, text }, { onSuccess: () => setText('') })}
            >
              {t('workEnvironmentPlan.review.add')}
            </Button>
          </div>
        </>
      )}
    </section>
  );
};
